import React, { useEffect, useState, useRef, Suspense, lazy, createElement } from 'react';
import { useMetaPixel } from './hooks/useMetaPixel';
import { StickyAlertBar } from './components/StickyAlertBar';
import { HeroSection } from './components/HeroSection';
// Lazy load below-fold sections
const RecipesSection = lazy(() => import('./components/RecipesSection').then(m => ({
  default: m.RecipesSection
})));
const PainPointsSection = lazy(() => import('./components/PainPointsSection').then(m => ({
  default: m.PainPointsSection
})));
const BenefitsSection = lazy(() => import('./components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
})));
const ProductBundle = lazy(() => import('./components/ProductBundle').then(m => ({
  default: m.ProductBundle
})));
const BonusSection = lazy(() => import('./components/BonusSection').then(m => ({
  default: m.BonusSection
})));
const PricingOffer = lazy(() => import('./components/PricingOffer').then(m => ({
  default: m.PricingOffer
})));
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const FAQSection = lazy(() => import('./components/FAQSection').then(m => ({
  default: m.FAQSection
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
// Renders children only when the sentinel enters the viewport
function ViewportGate({
  children,
  rootMargin = '200px',
  minHeight = '200px'
}: {
  children: React.ReactNode;
  rootMargin?: string;
  minHeight?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);
  if (visible) {
    return <>{children}</>;
  }
  return <div ref={ref} style={{
    minHeight
  }} data-id="element-0" />;
}
// Load Google Fonts non-blocking after first paint
function useNonBlockingFonts() {
  useEffect(() => {
    const load = () => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&family=Source+Sans+3:ital,wght@0,200..900;1,200..900&display=swap';
      document.head.appendChild(link);
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(load);
    } else {
      setTimeout(load, 100);
    }
  }, []);
}
export function App() {
  useMetaPixel();
  useNonBlockingFonts();
  return <div className="min-h-screen bg-brand-cream font-body text-brand-brown selection:bg-brand-terracotta selection:text-white" data-id="element-1">
      <StickyAlertBar data-id="element-2" />

      <main className="flex flex-col w-full overflow-x-hidden" data-id="element-3">
        <HeroSection data-id="element-4" />

        {/* First below-fold group — loads when user scrolls near */}
        <ViewportGate rootMargin="300px" data-id="element-5">
          <Suspense fallback={null} data-id="element-6">
            <RecipesSection data-id="element-7" />
            <PainPointsSection data-id="element-8" />
            <BenefitsSection data-id="element-9" />
          </Suspense>
        </ViewportGate>

        {/* Second group — testimonials + product */}
        <ViewportGate rootMargin="300px" data-id="element-10">
          <Suspense fallback={null} data-id="element-11">
            <SocialProof data-id="element-12" />
            <ProductBundle data-id="element-13" />
          </Suspense>
        </ViewportGate>

        {/* Third group — pricing-related */}
        <ViewportGate rootMargin="300px" data-id="element-14">
          <Suspense fallback={null} data-id="element-15">
            <BonusSection data-id="element-16" />
            <PricingOffer data-id="element-17" />
          </Suspense>
        </ViewportGate>

        {/* Fourth group — FAQ */}
        <ViewportGate rootMargin="200px" data-id="element-18">
          <Suspense fallback={null} data-id="element-19">
            <FAQSection data-id="element-20" />
          </Suspense>
        </ViewportGate>
      </main>

      <ViewportGate rootMargin="200px" data-id="element-21">
        <Suspense fallback={null} data-id="element-22">
          <Footer data-id="element-23" />
        </Suspense>
      </ViewportGate>
    </div>;
}